import { Button } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { BetaFeatureNotice } from 'src/core/common/components/BetaFeatureNotice';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useGetBudgetBreakdowns } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useGetBudgetBreakdowns';
import { type DimensionData } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimensionData';
import { BudgetPeriodTags } from 'src/core/modules/budgets/components/BudgetPeriodTags';

import { type BudgetDefinition } from '../../../../budgets-by-dimensions/hooks/budgetDefinition';
import { BudgetOverview } from '../../../../components/BudgetOverview';
import { type SpendingClickProperty } from '../../../hooks/useSpendingClick';
import { BudgetBreakdown } from '../../v1/BudgetBreakdown';
import { BudgetPeriodSelector } from '../../v1/BudgetPeriodSelector';
import { BudgetaryExercisesPageSkeleton } from '../../v1/NewBudgetaryExercisesPage/BudgetaryExercisesPageSkeleton';
import { BudgetsOverviewListV2 } from '../BudgetsOverviewListV2';
import { flattenBudgetTree } from '../helpers/misc';
import { makePeriod } from '../helpers/period';
import { usePeriodRanges } from '../hooks/usePeriodRanges';

type BudgetOverviewPageContentV2Props = {
  budgetDefinition: BudgetDefinition;
  dimensionData: DimensionData;
  spendingClick: SpendingClickProperty;
};

/**
 * BudgetOverviewPageContentV2 - Page content for Budget V2 overview
 *
 * This component renders the detailed view of a V2 budget, including:
 * - Budget overview with total amounts
 * - Budget breakdown by dimensions (cost centers, expense categories, etc.)
 * - Navigation back to budgets list
 */
export const BudgetOverviewPageContentV2 = ({
  budgetDefinition,
  dimensionData,
  spendingClick,
}: BudgetOverviewPageContentV2Props) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const companyId = useCompanyId();

  const location = useLocation();
  const selectedPeriodRanges = usePeriodRanges();
  const selectedPeriodRange = selectedPeriodRanges.getById(budgetDefinition.id);

  const [selectedDimensionId, setSelectedDimensionId] = useState<string>(
    location.state?.budgetRowId ?? 'root',
  );

  const selectedPeriod = makePeriod(
    budgetDefinition.period,
    selectedPeriodRange,
  );

  const breakdownsResponseQuery = useGetBudgetBreakdowns(
    budgetDefinition.id,
    selectedPeriod,
  );

  const breakdownsResponse = unwrapQuery(breakdownsResponseQuery);
  if (!breakdownsResponse) {
    return <BudgetaryExercisesPageSkeleton />;
  }

  const flattenedRows = flattenBudgetTree(
    breakdownsResponse.breakdowns,
    dimensionData,
    {
      budgetName: budgetDefinition.name,
    },
    t,
  );

  const selectedRow =
    flattenedRows.find((row) => row.id === selectedDimensionId) ??
    flattenedRows[0];

  const displayedBreakdown = selectedRow.breakdown;

  const handleDimensionSelect = (dimensionId: string) => {
    setSelectedDimensionId(dimensionId);
  };

  const title = selectedRow.label;

  return (
    <div className="page__container overflow-y-hidden">
      <HeaderWithNavigation
        links={[]}
        rightContent={
          <BetaFeatureNotice
            featureName={FEATURES.BUDGETS_BY_DIMENSIONS}
            feedbackTags={['budgets-v2']}
            showOnlyFeedbackModal
          />
        }
      >
        {t('navigation.budgets')}
      </HeaderWithNavigation>
      <div className="flex items-center justify-between px-40 py-24">
        <Button
          type="button"
          variant="tertiaryBrand"
          iconName="chevron-left"
          hasNegativeMargins
          text={t('budget.overviewPage.back')}
          onClick={() => {
            const pageRoute = routeFor(routes.BUDGETS_V2.path, {
              company: companyId,
            });
            history.push({
              pathname: pageRoute,
              state: {
                ...selectedPeriodRanges.getLocationState(),
                highlightedBudgetId: budgetDefinition.id,
              },
            });
          }}
        />
        <div className="flex items-center gap-16">
          <h1 className="title-l text-content-primary">
            {budgetDefinition.name}
          </h1>
          <BudgetPeriodTags period={budgetDefinition.period} />
        </div>
      </div>
      <div className="flex min-h-0 flex-1 gap-24 px-40">
        <BudgetsOverviewListV2
          budgetName={budgetDefinition.name}
          breakdowns={breakdownsResponse.breakdowns}
          dimensionData={dimensionData}
          isLoading={false}
          selectedItemId={selectedDimensionId}
          onSelectItem={handleDimensionSelect}
        />
        <div className="border-border-default mb-24 border-0 border-r border-solid" />
        <div className="flex min-h-0 flex-1 flex-col gap-24 overflow-y-auto pb-24">
          <div className="flex w-full items-center justify-between gap-16 px-16 py-12">
            <div className="flex items-center justify-between">
              <h2 className="title-m text-content-primary">{title}</h2>
            </div>
            <div className="flex items-center gap-8">
              <BudgetPeriodSelector
                range={selectedPeriodRange}
                maxRange={{
                  from: budgetDefinition.period.start,
                  to: budgetDefinition.period.end,
                }}
                onChange={(period) =>
                  selectedPeriodRanges.setById(budgetDefinition.id, period)
                }
              />
            </div>
          </div>
          <BudgetOverview
            name={title}
            breakdown={{
              ...displayedBreakdown,
              breakdownComputedAt: new Date(),
            }}
            amount={selectedRow.allocated}
            isUnavailable={false}
            mode="full"
          />
          <BudgetBreakdown
            budgetId={budgetDefinition.id}
            breakdown={displayedBreakdown}
            breakdownComputedAt={new Date()}
            breakdownParameters={{
              type: 'v2',
              period: selectedPeriod,
              nodePath: selectedRow.nodePath,
            }}
            spendingClick={spendingClick}
          />
        </div>
      </div>
    </div>
  );
};
